import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const services = [
  {
    icon: 'ri-home-4-line',
    title: 'Residential Painting',
    description: 'Complete interior and exterior painting for homes in Brackenfell',
    price: 'From R8,500',
    features: ['Premium quality paints', 'Surface preparation', 'Clean finish', '2-year warranty']
  },
  {
    icon: 'ri-building-line',
    title: 'Commercial Painting',
    description: 'Professional painting services for offices and retail spaces',
    price: 'From R12,000',
    features: ['Minimal disruption', 'After-hours available', 'Durable coatings', 'Fast turnaround']
  },
  {
    icon: 'ri-paint-brush-line',
    title: 'Roof Painting',
    description: 'Waterproof roof coatings and heat-reflective solutions',
    price: 'From R6,500',
    features: ['Waterproofing', 'Heat reflection', 'Leak prevention', '5-year guarantee']
  }
];

const paintingProcess = [
  { step: '1', title: 'Free Consultation', description: 'We assess your property and provide a detailed quote' },
  { step: '2', title: 'Preparation', description: 'Thorough surface prep and protection of your property' },
  { step: '3', title: 'Professional Painting', description: 'Expert application using premium materials' },
  { step: '4', title: 'Final Inspection', description: 'Quality check and walkthrough with you' }
];

const testimonials = [
  {
    rating: 5,
    text: 'Excellent service from start to finish. The team was professional, punctual, and the quality of work exceeded our expectations. Our Brackenfell home looks brand new!',
    name: 'Sarah van der Merwe',
    property: 'Brackenfell Homeowner',
    image: 'https://public.readdy.ai/ai/img_res/36431993d00c4b5516cbb1ed35da1cf4.jpg'
  },
  {
    rating: 5,
    text: 'Very impressed with the attention to detail and professionalism. They completed our office painting on time and within budget. Highly recommend for commercial projects.',
    name: 'Johan Botha',
    property: 'Business Owner',
    image: 'https://readdy.ai/api/search-image?query=Professional%20South%20African%20businessman%20portrait%20smiling%2C%20natural%20lighting%2C%20realistic%20photography&width=100&height=100&seq=testimonial-brackenfell-002&orientation=squarish'
  },
  {
    rating: 5,
    text: 'Outstanding workmanship! The exterior painting has transformed our property. The team was respectful, clean, and delivered exceptional results.',
    name: 'Linda Jacobs',
    property: 'Brackenfell Resident',
    image: 'https://readdy.ai/api/search-image?query=Professional%20South%20African%20woman%20portrait%20smiling%2C%20natural%20lighting%2C%20realistic%20photography&width=100&height=100&seq=testimonial-brackenfell-003&orientation=squarish'
  }
];

export default function BrackenfellPage() {
  useEffect(() => {
    document.title = 'Brackenfell Painting Services | Professional Painters Cape Town';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert painting services in Brackenfell, Cape Town. Residential, commercial, and industrial painting solutions. Professional painters with 15+ years experience. Free quotes available.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Brackenfell painters, painting services Brackenfell, house painters Brackenfell, commercial painters Cape Town, residential painting');
    }

    const metaGeoPosition = document.querySelector('meta[name="geo.position"]');
    if (metaGeoPosition) {
      metaGeoPosition.setAttribute('content', '-33.8698;18.7042');
    }

    const metaGeoPlacename = document.querySelector('meta[name="geo.placename"]');
    if (metaGeoPlacename) {
      metaGeoPlacename.setAttribute('content', 'Brackenfell, Cape Town, Western Cape, South Africa');
    }

    const metaGeoRegion = document.querySelector('meta[name="geo.region"]');
    if (metaGeoRegion) {
      metaGeoRegion.setAttribute('content', 'ZA-WC');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20luxury%20home%20in%20Brackenfell%20Cape%20Town%2C%20skilled%20painters%20on%20scaffolding%20applying%20paint%20to%20house%20walls%2C%20beautiful%20residential%20property%2C%20sunny%20day%2C%20professional%20equipment%2C%20high%20quality%20workmanship%2C%20realistic%20photography&width=1920&height=1080&seq=brackenfell-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Brackenfell
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Brackenfell's residential and commercial properties. Quality craftsmanship and reliable service for your home or business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </Link>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Area Description */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Painting Experts Serving Brackenfell</h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Brackenfell is a thriving residential and commercial hub in Cape Town's Northern Suburbs, known for its family-friendly atmosphere and growing business district. Our professional painting services cater to the diverse needs of Brackenfell's residential estates, commercial properties, and industrial facilities.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                With over 15 years of experience serving Brackenfell painting services, we understand the unique requirements of properties in this area. From modern residential complexes to established commercial centers, our team delivers exceptional painting solutions that enhance property value and aesthetic appeal.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Whether you need interior painting for your Brackenfell home, exterior painting for commercial buildings, or specialized industrial coatings, our skilled painters use premium materials and proven techniques to ensure lasting results that withstand Cape Town's coastal climate.
              </p>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20team%20working%20on%20residential%20house%20exterior%20in%20Brackenfell%20suburb%2C%20showing%20skilled%20craftsmen%20applying%20paint%20with%20brushes%20and%20rollers%2C%20modern%20house%20facade%2C%20quality%20painting%20equipment%2C%20bright%20daylight%2C%20professional%20service%20photography&width=600&height=500&seq=brackenfell-team-002&orientation=portrait"
                alt="Professional Painters in Brackenfell"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Offered */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Brackenfell Painting Services</h2>
            <p className="text-xl text-gray-600">Comprehensive painting solutions for every property type</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-black font-semibold text-lg mb-4">{service.price}</div>
                <ul className="text-sm text-gray-600 space-y-1">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <i className="ri-check-line mr-2" style={{color: '#ffff00'}}></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Brackenfell Residents Choose Us</h2>
            <p className="text-xl text-gray-600">Trusted painting professionals with proven expertise</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-award-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">15+ Years Experience</h3>
              <p className="text-gray-600">Extensive experience serving Brackenfell and Northern Suburbs</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-shield-check-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Quality Guaranteed</h3>
              <p className="text-gray-600">Premium materials and workmanship warranty</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-team-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Professional Team</h3>
              <p className="text-gray-600">Skilled and certified painting specialists</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-price-tag-3-line text-4xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Competitive Pricing</h3>
              <p className="text-gray-600">Transparent quotes with no hidden costs</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Painting Process</h2>
            <p className="text-xl text-gray-600">Simple, efficient, and professional from start to finish</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold" style={{backgroundColor: '#ffff00'}}>
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Recent Projects in Brackenfell</h2>
            <p className="text-xl text-gray-600">See our quality workmanship in action</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20residential%20house%20exterior%20with%20fresh%20white%20and%20grey%20paint%20finish%20in%20Brackenfell%20suburb%2C%20showing%20clean%20lines%2C%20contemporary%20design%2C%20well-maintained%20garden%2C%20professional%20painting%20work%2C%20bright%20natural%20lighting&width=400&height=300&seq=brackenfell-project-001&orientation=landscape"
                  alt="Residential Painting Project"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Modern Home Exterior</h3>
              <p className="text-gray-600">Complete exterior painting with premium weather-resistant coating</p>
            </div>
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Commercial%20office%20building%20facade%20with%20professional%20paint%20finish%20in%20Brackenfell%20business%20district%2C%20showing%20modern%20corporate%20architecture%2C%20clean%20painted%20surfaces%2C%20glass%20windows%2C%20professional%20commercial%20painting&width=400&height=300&seq=brackenfell-project-002&orientation=landscape"
                  alt="Commercial Painting Project"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Office Building Refresh</h3>
              <p className="text-gray-600">Professional commercial painting for local business center</p>
            </div>
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Residential%20interior%20living%20room%20with%20freshly%20painted%20walls%20in%20neutral%20tones%2C%20showing%20modern%20furniture%2C%20clean%20paint%20finish%2C%20natural%20lighting%20through%20windows%2C%20professional%20interior%20painting%20work%20in%20Brackenfell%20home&width=400&height=300&seq=brackenfell-project-003&orientation=landscape"
                  alt="Interior Painting Project"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Interior Transformation</h3>
              <p className="text-gray-600">Complete interior painting with modern color scheme</p>
            </div>
            <div className="group cursor-pointer">
              <div className="relative h-64 rounded-lg overflow-hidden mb-4 shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=House%20roof%20with%20professional%20waterproof%20coating%20and%20paint%20in%20Brackenfell%2C%20showing%20tiled%20roof%20with%20protective%20coating%2C%20clear%20blue%20sky%2C%20quality%20roofing%20work%2C%20residential%20property%20maintenance&width=400&height=300&seq=brackenfell-project-004&orientation=landscape"
                  alt="Roof Painting Project"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Roof Coating Service</h3>
              <p className="text-gray-600">Waterproof roof coating with heat-reflective properties</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Brackenfell Clients Say</h2>
            <p className="text-xl text-gray-600">Trusted by homeowners and businesses across Brackenfell</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-lg" style={{color: '#ffff00'}}></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{backgroundColor: '#ffff00'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Transform Your Brackenfell Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Brackenfell's trusted painting professionals. Get a free quote for your residential or commercial property.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-black text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Free Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}